import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BookingModal } from '../../components/booking-modal/booking-modal';
import { MetaService } from '../../services/meta.service';

interface ConcernCard {
	title: string;
	description: string;
}

interface Step {
	title: string;
	description: string;
}

interface Faq {
	question: string;
	answer: string;
}

interface RelatedService {
	id: number;
	service_name: string;
	slug: string;
	short_description: string;
	image_path: string;
}

interface ServiceDetailData {
	id: number;
	service_name: string;
	slug: string;
	tagline: string;
	short_description: string;
	description: string;
	image_path: string;
	meta_title: string;
	meta_description: string;
	// The JSON columns come back null when the admin has not filled a
	// section in, so every block is nullable, not just empty.
	intro_paragraphs: string[] | null;
	why_us_title: string | null;
	why_us_points: string[] | null;
	concerns_title: string | null;
	concerns: ConcernCard[] | null;
	how_it_works_title: string | null;
	how_it_works: Step[] | null;
	who_its_for: string | null;
	faqs: Faq[] | null;
	related_services: RelatedService[] | null;
	cta_text: string | null;
	cta_button_label: string | null;
	category: { id: number; name: string; slug: string } | null;
}

@Component({
	selector: 'app-service-detail',
	standalone: true,
	imports: [CommonModule, RouterLink, BookingModal],
	templateUrl: './service-detail.html',
	styleUrl: './service-detail.css',
})
export class ServiceDetail implements OnInit {
	private http = inject(HttpClient);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private metaService = inject(MetaService);

	service = signal<ServiceDetailData | null>(null);
	isLoading = signal(true);
	notFound = signal(false);
	showBookingModal = signal(false);
	openFaqIndex = signal<number | null>(0);

	storageUrl = environment.storageUrl;

	ngOnInit(): void {
		// paramMap rather than a one-off read, so navigating between two
		// service pages (via Related Services) reloads the content.
		this.route.paramMap.subscribe((params) => {
			const slug = params.get('slug');
			if (slug) {
				this.loadService(slug);
			}
		});
	}

	private loadService(slug: string): void {
		this.isLoading.set(true);
		this.notFound.set(false);
		this.openFaqIndex.set(0);

		this.http.get<any>(`${environment.apiUrl}/public/services/${slug}`).subscribe({
			next: (response) => {
				this.isLoading.set(false);
				if (response.status && response.data) {
					this.service.set(response.data);
					this.applyMeta(response.data);
					window.scrollTo({ top: 0, behavior: 'auto' });
				} else {
					this.notFound.set(true);
				}
			},
			error: () => {
				this.isLoading.set(false);
				this.notFound.set(true);
			},
		});
	}

	private applyMeta(service: ServiceDetailData): void {
		this.metaService.setCustomMeta(
			service.meta_title || service.service_name,
			service.meta_description || service.short_description || '',
		);
	}

	// ─── Headings, with the copy doc's wording as the fallback ───

	whyUsTitle(): string {
		const service = this.service();
		return service?.why_us_title || `Why The OG Hair Studio for ${service?.service_name ?? ''}`;
	}

	concernsTitle(): string {
		return this.service()?.concerns_title || 'Common Concerns We Address';
	}

	howItWorksTitle(): string {
		return this.service()?.how_it_works_title || 'How It Works';
	}

	ctaButtonLabel(): string {
		return this.service()?.cta_button_label || 'Book Your Consultation';
	}

	// ─── Interaction ───

	toggleFaq(index: number): void {
		this.openFaqIndex.set(this.openFaqIndex() === index ? null : index);
	}

	openBookingModal(): void {
		this.showBookingModal.set(true);
	}

	closeBookingModal(): void {
		this.showBookingModal.set(false);
	}

	backToServices(): void {
		this.router.navigate(['/services']);
	}

	getImageUrl(path: string | null | undefined): string {
		if (!path) {
			return '';
		}
		if (path.startsWith('http') || path.startsWith('data:')) {
			return path;
		}
		return `${this.storageUrl}/${path}`;
	}

	onImageError(event: Event): void {
		(event.target as HTMLImageElement).style.display = 'none';
	}
}
